import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteLoaderData,
} from "@remix-run/react";
import { json } from "@vercel/remix";
import type { LinksFunction, MetaFunction } from "@vercel/remix";
import tailwind from "@figment-fitness/ui/styles/tailwind.css?url";

import "@figment-fitness/ui"; // for importing style.css
import "@figment-fitness/ui/dist/index.css"; // for importing style.css
import "@radix-ui/themes/styles.css";
import { Theme } from "@figment-fitness/ui/components/theme";
import { initConfig } from "~/client-config";
import { Toaster } from "@figment-fitness/ui/components/toast";
import DefaultErrorBoundary from "@figment-fitness/ui/components/error-boundary";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: tailwind },
];

export const loader = async () => {
  return json({
    maintenance: process.env.MAINTENANCE_MODE === "1",
    ENV: {
      PUBLIC_APP_ENV: process.env.PUBLIC_APP_ENV,
    },
  });
};

function MaintenanceScreen() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        textAlign: "center",
      }}
    >
      <div style={{ maxWidth: "480px" }}>
        <h1 style={{ fontSize: "24px", fontWeight: 700, marginBottom: "12px" }}>
          メンテナンス中です
        </h1>
        <p style={{ color: "#555", lineHeight: 1.7 }}>
          ただいまシステムメンテナンスを実施しています。
          <br />
          ご不便をおかけしますが、しばらく経ってから再度アクセスしてください。
        </p>
      </div>
    </div>
  );
}

export const meta: MetaFunction = () => {
  return [
    { title: "Figment-フィットネス版" },
    { name: "description", content: "Figment system" },
  ];
};

export const config = {
  maxDuration: 30,
  memory: 1024,
};

export function Layout({ children }: { children: React.ReactNode }) {
  // Layout はエラー発生時（ErrorBoundary 表示時）も描画されるが、その際は
  // root loader のデータが存在しない。useLoaderData はその場合に例外を
  // 投げてしまう（"You cannot useLoaderData in an errorElement"）ため、
  // undefined を安全に返す useRouteLoaderData を使う。
  const data = useRouteLoaderData<typeof loader>("root");
  const appENV = data?.ENV?.PUBLIC_APP_ENV;
  if (appENV) initConfig(appENV);

  return (
    <html lang="ja">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <Theme>
          {children}
          <Toaster />
        </Theme>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = DefaultErrorBoundary;

export default function App() {
  const data = useLoaderData<typeof loader>();
  if (data?.maintenance) return <MaintenanceScreen />;
  return <Outlet />;
}
